import { useState } from 'react';
import {
  Box,
  Text,
  VStack,
  HStack,
  Input,
  Button,
  Checkbox,
  useColorModeValue,
  Badge,
  Icon,
} from '@chakra-ui/react';
import { Trash2, Building2, FileText } from 'lucide-react';
import { L10Note, User, Department } from '../../types';
import { useAuth } from '../../../../contexts/AuthContext';

interface SectionNotesEditorProps {
  sectionKey: string;
  sectionLabel: string;
  sectionColor: string;
  hintText: string;
  description: string;
  notes: L10Note[];
  users: User[];
  departments: Department[];
  onAddNote: (
    note: string,
    category: 'general' | 'department_communication',
    owners?: string[],
    departments?: string[]
  ) => Promise<void>;
  onDeleteNote: (noteId: string) => Promise<void>;
  isPresenter: boolean;
  isDisabled?: boolean;
}

export function SectionNotesEditor({
  sectionKey,
  sectionLabel,
  sectionColor,
  hintText,
  description,
  notes,
  users,
  departments,
  onAddNote,
  onDeleteNote,
  isPresenter,
  isDisabled = false,
}: SectionNotesEditorProps) {
  const { eosUsers = [] } = useAuth();
  const [newNoteText, setNewNoteText] = useState('');
  const [noteCategory, setNoteCategory] = useState<'general' | 'department_communication'>('general');
  const [selectedOwners, setSelectedOwners] = useState<Set<string>>(new Set());
  const [selectedDepartments, setSelectedDepartments] = useState<Set<string>>(new Set());
  const [isAddingNote, setIsAddingNote] = useState(false);

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue(`${sectionColor}.200`, `${sectionColor}.700`);
  const textColor = useColorModeValue(`${sectionColor}.900`, `${sectionColor}.100`);
  const hintColor = useColorModeValue(`${sectionColor}.600`, `${sectionColor}.400`);
  const descColor = useColorModeValue(`${sectionColor}.700`, `${sectionColor}.300`);
  const lightBg = useColorModeValue(`${sectionColor}.50`, `${sectionColor}.900`);

  const handleToggleOwner = (userId: string) => {
    const newSet = new Set(selectedOwners);
    if (newSet.has(userId)) {
      newSet.delete(userId);
    } else {
      newSet.add(userId);
    }
    setSelectedOwners(newSet);
  };

  const handleToggleDepartment = (deptId: string) => {
    const newSet = new Set(selectedDepartments);
    if (newSet.has(deptId)) {
      newSet.delete(deptId);
    } else {
      newSet.add(deptId);
    }
    setSelectedDepartments(newSet);
  };

  const handleAddNote = async () => {
    if (!newNoteText.trim()) return;

    if (noteCategory === 'department_communication' && selectedOwners.size === 0 && selectedDepartments.size === 0) {
      // Require at least one owner or department for dept communication
      return;
    }

    setIsAddingNote(true);
    try {
      const owners = noteCategory === 'department_communication' ? Array.from(selectedOwners) : undefined;
      const depts = noteCategory === 'department_communication' ? Array.from(selectedDepartments) : undefined;

      await onAddNote(newNoteText, noteCategory, owners, depts);

      // Reset form
      setNewNoteText('');
      setNoteCategory('general');
      setSelectedOwners(new Set());
      setSelectedDepartments(new Set());
    } catch (error) {
      console.error('Error adding note:', error);
    } finally {
      setIsAddingNote(false);
    }
  };

  const getUserName = (userId: string) => {
    // Try to find in eosUsers first, then fall back to users prop if provided
    const user = eosUsers.find((u) => u.id === userId || u.uid === userId) || 
                 (users && users.find((u) => u.id === userId || u.uid === userId));
    return user?.name || `User ${userId}`;
  };

  const getDepartmentName = (deptId: string) => {
    const dept = departments.find((d) => d.id === deptId);
    return dept?.name || `Department ${deptId}`;
  };

  return (
    <Box
      borderWidth={2}
      borderColor={borderColor}
      borderRadius="lg"
      bg={lightBg}
      p={4}
    >
      <HStack justify="space-between" mb={3} alignItems="center">
        <Text fontSize="sm" fontWeight="bold" color={textColor} textTransform="uppercase" letterSpacing="wide">
          {sectionLabel}
        </Text>
        {isPresenter && !isDisabled && (
          <Text fontSize="xs" color={hintColor} fontWeight="medium">
            {hintText}
          </Text>
        )}
      </HStack>

      <Text fontSize="xs" color={descColor} mb={4}>
        {description}
      </Text>

      {/* Add note input section */}
      {isPresenter && !isDisabled && (
        <Box mb={4} pt={4} borderTopWidth={1} borderColor={borderColor}>
          <VStack spacing={3} align="stretch">
            <Input
              value={newNoteText}
              onChange={(e) => setNewNoteText(e.target.value)}
              placeholder="Add note for this section"
              size="sm"
              bg="white"
            />

            <HStack spacing={4}>
              <Checkbox
                isChecked={noteCategory === 'general'}
                onChange={(e) => {
                  if (e.target.checked) {
                    setNoteCategory('general');
                    setSelectedOwners(new Set());
                    setSelectedDepartments(new Set());
                  }
                }}
                size="sm"
              >
                <Text fontSize="sm">General Note</Text>
              </Checkbox>
              <Checkbox
                isChecked={noteCategory === 'department_communication'}
                onChange={(e) => {
                  setNoteCategory(e.target.checked ? 'department_communication' : 'general');
                  if (!e.target.checked) {
                    setSelectedOwners(new Set());
                    setSelectedDepartments(new Set());
                  }
                }}
                size="sm"
              >
                <Text fontSize="sm">Department Communication</Text>
              </Checkbox>
            </HStack>

            {noteCategory === 'department_communication' && (
              <HStack spacing={4} align="start" mt={2} flexWrap="wrap">
                <Box flex={1} minW="200px">
                  <Text fontSize="xs" fontWeight="semibold" mb={2} color={textColor}>
                    Owner(s):
                  </Text>
                  <VStack spacing={1} align="stretch" maxH="120px" overflowY="auto" borderWidth={1} borderColor={borderColor} borderRadius="md" p={2} bg="white">
                    {eosUsers.map((user) => (
                      <Checkbox
                        key={user.id}
                        isChecked={selectedOwners.has(user.id)}
                        onChange={() => handleToggleOwner(user.id)}
                        size="sm"
                      >
                        <Text fontSize="sm">{user.name}</Text>
                      </Checkbox>
                    ))}
                    {eosUsers.length === 0 && (
                      <Text fontSize="xs" color="gray.500" fontStyle="italic">
                        Select users to assign
                      </Text>
                    )}
                  </VStack>
                </Box>

                <Box flex={1} minW="200px">
                  <Text fontSize="xs" fontWeight="semibold" mb={2} color={textColor}>
                    Department(s):
                  </Text>
                  <VStack spacing={1} align="stretch" maxH="120px" overflowY="auto" borderWidth={1} borderColor={borderColor} borderRadius="md" p={2} bg="white">
                    {departments.map((dept) => (
                      <Checkbox
                        key={dept.id}
                        isChecked={selectedDepartments.has(dept.id)}
                        onChange={() => handleToggleDepartment(dept.id)}
                        size="sm"
                      >
                        <Text fontSize="sm">{dept.name}</Text>
                      </Checkbox>
                    ))}
                    {departments.length === 0 && (
                      <Text fontSize="xs" color="gray.500" fontStyle="italic">
                        Select departments to assign
                      </Text>
                    )}
                  </VStack>
                </Box>
              </HStack>
            )}

            <Button
              onClick={handleAddNote}
              isDisabled={!newNoteText.trim() || isAddingNote || (noteCategory === 'department_communication' && selectedOwners.size === 0 && selectedDepartments.size === 0)}
              size="sm"
              colorScheme={sectionColor}
              alignSelf="flex-end"
            >
              {isAddingNote ? 'Adding...' : 'Add to Notes'}
            </Button>
          </VStack>
        </Box>
      )}

      {/* Display all notes as cards */}
      {notes.length > 0 && (
        <Box maxH="400px" overflowY="auto" mt={4} pr={1}>
          <VStack spacing={2} align="stretch">
            {notes.map((note) => (
              <Box
              key={note.id}
              borderWidth={1}
              borderColor={borderColor}
              borderRadius="md"
              p={3}
              bg="white"
              position="relative"
            >
              <HStack justify="space-between" align="start" mb={2}>
                <Badge
                  colorScheme={note.noteCategory === 'department_communication' ? 'blue' : 'gray'}
                  fontSize="xs"
                  px={2}
                  py={1}
                >
                  <HStack spacing={1}>
                    <Icon
                      as={note.noteCategory === 'department_communication' ? Building2 : FileText}
                      w={3}
                      h={3}
                    />
                    <Text>
                      {note.noteCategory === 'department_communication' ? 'Dept Communication' : 'General'}
                    </Text>
                  </HStack>
                </Badge>
                {isPresenter && !isDisabled && (
                  <Button
                    size="xs"
                    variant="ghost"
                    colorScheme="red"
                    onClick={() => onDeleteNote(note.id)}
                    minW="auto"
                    h="auto"
                    p={1}
                  >
                    <Icon as={Trash2} w={4} h={4} />
                  </Button>
                )}
              </HStack>
              <Text fontSize="sm" color="gray.800" whiteSpace="pre-wrap" mb={note.noteCategory === 'department_communication' ? 2 : 0}>
                {note.notes}
              </Text>
              {note.noteCategory === 'department_communication' && (
                <VStack spacing={1} align="start" mt={2}>
                  {note.assignedUsers && note.assignedUsers.length > 0 && (
                    <Text fontSize="xs" color="gray.600">
                      Owners: {note.assignedUsers.map((uid) => getUserName(uid as string)).join(', ')}
                    </Text>
                  )}
                  {note.assignedDepartments && note.assignedDepartments.length > 0 && (
                    <Text fontSize="xs" color="gray.600">
                      Departments: {note.assignedDepartments.map((deptId) => getDepartmentName(deptId as string)).join(', ')}
                    </Text>
                  )}
                </VStack>
              )}
            </Box>
            ))}
          </VStack>
        </Box>
      )}
    </Box>
  );
}

